import React, { useState } from 'react';
// 🎫 TDZ-FIX: Import directo con alias para evitar conflicto con minificación de variable 'S'
import { 
  Sparkles as SparklesIcon, 
  AlertTriangle, 
  ThumbsUp, 
  Lightbulb, 
  MessageSquareMore, 
  ChevronRight, 
  ChevronDown 
} from 'lucide-react';
import { marked } from 'marked';

/**
 * CoachHintBubble - Foco amarillo que muestra sugerencias del coach
 * Reemplaza el modal para cumplir con UX V3
 * 
 * @param {Object} coachAlert - { hasSuggestion, suggestion, type, reason, action }
 * @param {String} messageId - ID del mensaje al que está asociado
 * @param {Boolean} isExpanded - Estado expandido controlado externamente (opcional)
 * @param {Function} onToggle - Callback para cambiar estado (opcional)
 */
const CoachHintBubble = ({ coachAlert, messageId, isExpanded: externalExpanded, onToggle }) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  

  
  // Usar control externo si está disponible, sino usar interno
  const isExpanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;
  const toggleExpanded = onToggle || (() => setInternalExpanded(!internalExpanded));

  if (!coachAlert?.hasSuggestion) return null;

  const getIcon = () => {
    switch (coachAlert.type) {
      case 'correction':
        return <AlertTriangle className="w-5 h-5" />;
      case 'praise':
        return <ThumbsUp className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'tip':
      default:
        return <SparklesIcon className="w-5 h-5" />; // 🎫 TDZ-FIX: Usando alias
    }
  };

  const getColors = () => {
    switch (coachAlert.type) {
      case 'correction':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-500',
          focusBg: 'bg-red-500',
          focusHover: 'hover:bg-red-600'
        };
      case 'praise':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: 'text-green-500',
          focusBg: 'bg-green-500',
          focusHover: 'hover:bg-green-600'
        };
      case 'warning':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          icon: 'text-orange-500',
          focusBg: 'bg-orange-500',
          focusHover: 'hover:bg-orange-600'
        };
      case 'tip':
      default:
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: 'text-yellow-600',
          focusBg: 'bg-yellow-500',
          focusHover: 'hover:bg-yellow-600'
        };
    }
  };

  const colors = getColors();

  const getLabel = () => {
    switch (coachAlert.type) {
      case 'correction': return 'Mejora Necesaria';
      case 'praise': return 'Excelente';
      case 'warning': return 'Atención';
      case 'tip': return 'Recomendación';
      default: return 'Coach';
    }
  };

  // Determinar si es una acción crítica (pausar/cerrar sesión)
  const isCriticalAction = coachAlert.action === 'pause_session' || coachAlert.action === 'end_session';

  return (
    <div className="mt-2 mb-1 max-w-2xl">
      {/* Estado contraído - Preview clickeable */}
      {!isExpanded && (
        <button
          onClick={toggleExpanded}
          className={`
            w-full flex items-center gap-2 px-3 py-2 rounded-lg
            ${colors.bg} ${colors.border} border-2
            transition-all duration-200 shadow-sm hover:shadow-md
            ${isCriticalAction ? 'animate-pulse' : ''}
          `}
          aria-label={`${getLabel()} del coach - Click para expandir`}
        >
          <div className={`flex-shrink-0 ${colors.icon}`}>
            {getIcon()}
          </div>
          <span className="font-semibold text-xs text-gray-700">💡 COACH</span>
          <span className="text-xs text-gray-600 truncate flex-1 text-left">
            {coachAlert.suggestion?.substring(0, 60)}...
          </span>
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        </button>
      )}

      {/* Estado expandido - Contenido completo */}
      {isExpanded && (
        <div 
          className={`
            rounded-lg border-2 ${colors.border} ${colors.bg}
            p-4 shadow-lg animate-slideDown
          `}
        >
          {/* Header con botón contraer */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`${colors.icon}`}>
                {getIcon()}
              </div>
              <span className={`${colors.icon} font-semibold text-sm`}>
                💡 {getLabel()}
              </span>
            </div>
            <button
              onClick={toggleExpanded}
              className="p-1.5 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
              aria-label="Contraer sugerencia"
            >
              <ChevronDown className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Sugerencia con formato Markdown */}
          <div 
            className="text-sm text-gray-800 leading-relaxed mb-3 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: marked.parse(coachAlert.suggestion || "") }}
          />

          {/* Razón (opcional) - COMENTADO porque es redundante */}
          {/* {coachAlert.reason && (
            <div className="p-3 bg-white bg-opacity-60 rounded border border-blue-200 mb-2">
              <p className="text-xs font-semibold text-blue-700 uppercase flex items-center gap-1 mb-1">
                <MessageSquareMore className="w-3.5 h-3.5" />
                ¿Por qué es importante?
              </p>
              <p className="text-xs text-gray-700">{coachAlert.reason}</p>
            </div>
          )} */}

          {/* Acción crítica (pausar/cerrar) */}
          {isCriticalAction && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-lg">
              <p className="text-xs font-bold text-red-800">
                {coachAlert.action === 'pause_session' ? '⏸️ Sesión pausada' : '🛑 Sesión finalizada'}
              </p>
              <p className="text-xs text-red-700 mt-1">
                {coachAlert.action === 'pause_session' 
                  ? 'El coach ha decidido pausar la conversación. Revisa el feedback antes de continuar.'
                  : 'El coach ha finalizado la sesión. No se pueden enviar más mensajes.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachHintBubble;
